import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { tCloseLeadRef } from './LeadClose.context';
import { closeMenuEntries } from './LeadClose.functions';
import type { tMoneyTotal } from './LeadClose.types';
import { useLeadClose } from './useLeadClose.hook';
import './LeadClosePanelRows.styles.scss';

/** Ganancia's lines: the main currency first, then the rest as they came. */
function orderedTotals(
  totals: tMoneyTotal[],
  main: string | undefined,
): tMoneyTotal[] {
  return [...totals].sort(
    (a, b) => Number(b.currency === main) - Number(a.currency === main),
  );
}

/**
 * The lead panel's "Resultado" row and Ganancia (U4), shown in place of the
 * editable Ganancia while `leads_lead-close` is on. Ganancia is read-only:
 * it is the sum of the lead's sales, one line per currency.
 */
export default function LeadClosePanelRows(props: {
  lead: tCloseLeadRef;
}): JSX.Element {
  const { lead } = props;
  const { t } = useTranslation('chat/chat');
  const { summaryOf, loadSummary, openForm, settings } = useLeadClose();
  const summary = summaryOf(lead.id);

  useEffect(() => {
    if (summary.status === 'idle') loadSummary(lead.id);
  }, [lead.id, summary.status, loadSummary]);

  const current = summary.data?.current ?? null;
  const totals = orderedTotals(
    summary.data?.totals ?? [],
    settings.data?.sales_currency,
  );

  return (
    <>
      <div className="ChatInfoAttributes__Content__Item LeadClosePanelRows__Item">
        <p>{t('leadClose.panel.result')}:</p>
        <div className="LeadClosePanelRows__Value">
          {summary.status === 'failed' ? (
            <button
              type="button"
              className="LeadClosePanelRows__Action"
              onClick={() => loadSummary(lead.id)}
            >
              {t('leadClose.form.retry')}
            </button>
          ) : summary.status !== 'ready' ? (
            <span className="LeadClosePanelRows__Muted">
              {t('leadClose.panel.loading')}
            </span>
          ) : (
            <>
              <span className="LeadClosePanelRows__Current">
                {current
                  ? t(`leadClose.result.${current.type}`)
                  : t('leadClose.panel.none')}
              </span>
              <span className="LeadClosePanelRows__Actions">
                {closeMenuEntries(summary.data).map((entry) => (
                  <button
                    key={entry.label}
                    type="button"
                    className="LeadClosePanelRows__Action"
                    onClick={() =>
                      openForm({ lead, type: entry.type, source: 'lead_panel' })
                    }
                  >
                    {t(`leadClose.menu.${entry.label}`)}
                  </button>
                ))}
              </span>
            </>
          )}
        </div>
      </div>

      <div className="ChatInfoAttributes__Content__Item LeadClosePanelRows__Item">
        <p>{t('attributes.revenue')}:</p>
        <div className="LeadClosePanelRows__Value ph-no-capture">
          {totals.length === 0 ? (
            <span className="LeadClosePanelRows__Revenue">$0</span>
          ) : (
            totals.map((total) => (
              <span
                key={total.currency}
                className="LeadClosePanelRows__Revenue"
                data-testid="lead-close-revenue-line"
              >
                ${total.amount} {total.currency}
              </span>
            ))
          )}
        </div>
      </div>
    </>
  );
}
